import { useId, type FC } from 'react'
import type { DocumentStatus } from '@project/contracts'

/**
 * Status is a drawn shape first, then the word, then the colour. The glyphs are
 * distinguishable in greyscale and to a dichromat, because these documents get
 * printed on a site office laser printer and read on a phone in sunlight.
 */

const WORDS: Record<DocumentStatus, string> = {
  DRAFT: 'Draft',
  IN_REVIEW: 'In review',
  APPROVED: 'Approved',
  CANCELLED: 'Cancelled',
}

const MODIFIERS: Record<DocumentStatus, string> = {
  DRAFT: 'status--draft',
  IN_REVIEW: 'status--in-review',
  APPROVED: 'status--approved',
  CANCELLED: 'status--cancelled',
}

export interface StatusMarkProps {
  status: DocumentStatus
  size?: number
}

export const StatusMark: FC<StatusMarkProps> = ({ status, size = 18 }) => {
  const maskId = useId()

  return (
    <span className={`status ${MODIFIERS[status]}`}>
      <span className="status__glyph">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={size}
          height={size}
          viewBox="0 0 24 24"
          aria-hidden="true"
          focusable="false"
          style={{ display: 'block' }}
        >
          {status === 'DRAFT' && (
            <circle
              cx="12"
              cy="12"
              r="8.4"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeDasharray="3.4 3.2"
              strokeLinecap="round"
            />
          )}

          {status === 'IN_REVIEW' && (
            <>
              <path d="M3.6 12a8.4 8.4 0 0 0 16.8 0z" fill="currentColor" />
              <circle cx="12" cy="12" r="8.4" fill="none" stroke="currentColor" strokeWidth="2" />
            </>
          )}

          {status === 'APPROVED' && (
            <>
              <mask id={maskId}>
                <rect x="0" y="0" width="24" height="24" fill="white" />
                <path
                  d="m7.6 12.3 3 3.1 5.9-6.6"
                  fill="none"
                  stroke="black"
                  strokeWidth="2.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </mask>
              <circle cx="12" cy="12" r="9.2" fill="currentColor" mask={`url(#${maskId})`} />
            </>
          )}

          {status === 'CANCELLED' && (
            <>
              <circle cx="12" cy="12" r="8.4" fill="none" stroke="currentColor" strokeWidth="2" />
              <path d="M6.9 17.1 17.1 6.9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </>
          )}
        </svg>
      </span>
      <span className="status__word">{WORDS[status]}</span>
    </span>
  )
}
